import { type PartialDeep } from 'type-fest';

import {
  fixedListLayoutMode,
  forcedCustomTemplateLayoutMode,
  type ListDirectionMode,
  type ListLayoutMode,
  ListScrollMode,
  resolveListLayoutFields,
} from '@blaze-shared/elements-domain';

import { type ListImagePropertiesModel } from '../list.models';
import {
  type ListTemplateImageContext,
  resolveListTemplateImage,
} from '../list.template-appearance';

import {
  toEngineEmptyImageThumbnail,
  toEngineImageBehavior,
  toEngineImageShape,
  toEngineListDirection,
  toEngineListLayoutMode,
  toEngineListScrollMode,
} from './list.appearance';
import { type ListBehavior, type ListMobileBehavior, type ListTemplate } from './list.definition';

export interface ResolvedListTemplate {
  direction: ListDirectionMode;
  mode?: ListLayoutMode;
  rows?: number;
  cols?: number;
  mobileScrollMode?: ListScrollMode;
  bulletIcon?: string;
}

export interface ListTemplateLayoutOptions {
  behavior?: ListBehavior;
  mobileBehavior?: ListMobileBehavior;
  rows?: number;
  columns?: number;
}

export function resolveListTemplate(
  template: ListTemplate,
  options: ListTemplateLayoutOptions = {},
): ResolvedListTemplate {
  const direction = toEngineListDirection(template.$type);
  const mode = resolveListLayoutMode(direction, options.behavior);
  const layoutFields = resolveListLayoutFields(direction, mode);
  const result: ResolvedListTemplate = { direction };

  if (mode) {
    result.mode = mode;
  }

  if (layoutFields.columnsApply) {
    if (options.columns !== undefined) {
      result.cols = options.columns;
    } else if (direction === 'custom') {
      result.cols = 1;
    }
  }

  if (layoutFields.rowsApply && options.rows !== undefined) {
    result.rows = options.rows;
  }

  const mobileScrollMode = resolveMobileScrollMode(direction, mode, options.mobileBehavior);
  if (mobileScrollMode) {
    result.mobileScrollMode = mobileScrollMode;
  }

  if (template.$type === 'subscription' && template.bulletIconName) {
    result.bulletIcon = template.bulletIconName;
  }

  return result;
}

/**
 * templates with a fixed layout mode keep it; every other direction honours `behavior`.
 */
function resolveListLayoutMode(
  direction: ListDirectionMode,
  behavior: ListBehavior | undefined,
): ListLayoutMode | undefined {
  const fixedMode = fixedListLayoutMode(direction);
  if (fixedMode) {
    return fixedMode;
  }

  if (!behavior) {
    return undefined;
  }

  const mode = toEngineListLayoutMode(behavior);
  return forcedCustomTemplateLayoutMode(direction, mode) ?? mode;
}

function resolveMobileScrollMode(
  direction: ListDirectionMode,
  mode: ListLayoutMode | undefined,
  mobileBehavior: ListMobileBehavior | undefined,
): ListScrollMode | undefined {
  if (direction === 'notification') {
    return undefined;
  }

  if (mobileBehavior) {
    return toEngineListScrollMode(mobileBehavior);
  }

  return mode === 'hscroll' ? ListScrollMode.Swipe : ListScrollMode.Scroll;
}

export function resolveListTemplateImageAppearance(
  template: ListTemplate,
  context: ListTemplateImageContext,
): PartialDeep<ListImagePropertiesModel> {
  const direction = toEngineListDirection(template.$type);
  const appearance: PartialDeep<ListImagePropertiesModel> = resolveListTemplateImage(
    direction,
    context,
  );

  if (template.$type === 'html') {
    return appearance;
  }

  if (template.imageShape) {
    appearance.shape = toEngineImageShape(template.imageShape);
  }

  if (template.imageBehavior) {
    appearance.behavior = toEngineImageBehavior(template.imageBehavior);
  }

  if (template.radius !== undefined) {
    appearance.radius = `${template.radius}px`;
  }

  if (template.width !== undefined) {
    appearance.outlineWidth = `${template.width}px`;
  }

  if (template.emptyImageState) {
    appearance.emptyThumbnail = toEngineEmptyImageThumbnail(template.emptyImageState);
  }

  return appearance;
}
